/**
 * Weights API Client
 *
 * API client for weight records endpoints
 * Base URL: https://linkeddog-api.up.railway.app
 * Updated: 2025-12-08 - Aligned with OpenAPI spec
 */

import { apiClient } from './client';
import type {
  Weight,
  WeightsListResponse,
  CreateWeightRequest,
  CreateWeightResponse,
  UpdateWeightRequest,
} from '@/shared/types/api.types';

export const weightsApi = {
  /**
   * Get all weight records for a dog (paginated)
   * Endpoint: GET /dogs/{dogId}/weights
   */
  getAll: async (
    dogId: string,
    page = 1,
    pageSize = 20
  ): Promise<WeightsListResponse> => {
    const response = await apiClient.get<WeightsListResponse>(
      `/dogs/${dogId}/weights`,
      { params: { page, pageSize } }
    );
    return response.data;
  },

  /**
   * Get the last weight record for a dog
   * Endpoint: GET /dogs/{dogId}/weights/last
   */
  getLast: async (dogId: string): Promise<Weight | null> => {
    try {
      const response = await apiClient.get<Weight>(
        `/dogs/${dogId}/weights/last`
      );
      return response.data;
    } catch (_error) {
      // 404 means no weight records exist
      return null;
    }
  },

  /**
   * Create a new weight record
   * Endpoint: POST /dogs/{dogId}/weights
   * Response 201: { weightId: string }
   */
  create: async (
    dogId: string,
    data: CreateWeightRequest
  ): Promise<CreateWeightResponse> => {
    const response = await apiClient.post<CreateWeightResponse>(
      `/dogs/${dogId}/weights`,
      data
    );
    return response.data;
  },

  /**
   * Update a weight record
   * Endpoint: PUT /dogs/{dogId}/weights/{weightId}
   * Response 204: No Content
   */
  update: async (
    dogId: string,
    weightId: string,
    data: UpdateWeightRequest
  ): Promise<void> => {
    await apiClient.put(`/dogs/${dogId}/weights/${weightId}`, data);
  },

  /**
   * Delete a weight record
   * Endpoint: DELETE /dogs/{dogId}/weights/{weightId}
   * Response 204: No Content
   */
  delete: async (dogId: string, weightId: string): Promise<void> => {
    await apiClient.delete(`/dogs/${dogId}/weights/${weightId}`);
  },
};
